import { setOfflineOnlineFlag } from "@/offline/networkFlag";
import NetInfo, { NetInfoState } from "@react-native-community/netinfo";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Platform } from "react-native";

type NetworkContextValue = {
  isOnline: boolean;
  isNetworkStateKnown: boolean;
  /** Nur in __DEV__: Offline-Zustand simulieren (zum Testen ohne Netzwerk zu trennen). */
  simulateOffline: boolean;
  setSimulateOffline: (value: boolean) => void;
};

const NetworkContext = createContext<NetworkContextValue>({
  isOnline: true,
  isNetworkStateKnown: false,
  simulateOffline: false,
  setSimulateOffline: () => {},
});

export function useNetworkStatus(): NetworkContextValue {
  const ctx = useContext(NetworkContext);
  if (!ctx) {
    throw new Error("useNetworkStatus must be used within NetworkProvider");
  }
  return ctx;
}

export function NetworkProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<NetInfoState | null>(null);
  const [simulateOffline, setSimulateOffline] = useState(false);
  const [browserOnline, setBrowserOnline] = useState<boolean | null>(null);

  const handleStateChange = useCallback((next: NetInfoState) => {
    setState(next);
  }, []);

  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(handleStateChange);
    NetInfo.fetch().then(setState);
    return () => unsubscribe();
  }, [handleStateChange]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    const onOnline = () => setBrowserOnline(true);
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    setBrowserOnline(typeof navigator !== "undefined" ? navigator.onLine : true);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const value = useMemo(() => {
    const known = state != null;
    const realOnline =
      state?.type !== "none" &&
      state?.isConnected === true &&
      state?.isInternetReachable !== false;
    const online = simulateOffline
      ? false
      : browserOnline != null
        ? browserOnline
        : known
          ? realOnline
          : true;
    return {
      isOnline: online,
      isNetworkStateKnown: known || browserOnline != null,
      simulateOffline,
      setSimulateOffline,
    };
  }, [state, simulateOffline, browserOnline]);

  useEffect(() => {
    setOfflineOnlineFlag(value.isOnline);
  }, [value.isOnline]);

  return (
    <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>
  );
}
